import { useEffect, useState } from 'react';
import {
  Shield,
  Users,
  Mail,
  Lock,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Activity,
} from 'lucide-react';
import { membersApi } from '../../../api/client';
import type { OrgSummary } from '../../../api/client';
import { usePermissions } from '../../../hooks/usePermissions';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface SecurityCheckProps {
  label: string;
  description: string;
  status: 'ok' | 'warn' | 'info';
}

function SecurityCheck({ label, description, status }: SecurityCheckProps) {
  const STYLES = {
    ok:   { icon: CheckCircle, color: 'text-win',  bg: 'bg-win/10',  border: 'border-win/20' },
    warn: { icon: AlertTriangle, color: 'text-warn', bg: 'bg-warn/10', border: 'border-warn/20' },
    info: { icon: Activity,    color: 'text-brand-light', bg: 'bg-brand/10', border: 'border-brand/20' },
  };
  const { icon: Icon, color, bg, border } = STYLES[status];
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${bg} ${border}`}>
      <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${color}`} />
      <div>
        <p className={`text-sm font-medium ${color}`}>{label}</p>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
    </div>
  );
}

export function AdminSecurityPage() {
  const { can, hasRole } = usePermissions();
  const canView = can('organization.view');

  const [summary, setSummary] = useState<OrgSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canView) return;
    setIsLoading(true);
    membersApi
      .getSummary()
      .then(setSummary)
      .catch((err) => setError((err as Error).message ?? 'Failed to load organization data'))
      .finally(() => setIsLoading(false));
  }, [canView]);

  if (!canView) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <p className="text-sm text-gray-500">You don't have permission to view security settings.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-100">Security</h2>
        <p className="text-sm text-gray-500 mt-1">
          Organization security posture and access controls.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-3 bg-loss/10 border border-loss/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-loss flex-shrink-0" />
          <p className="text-sm text-loss">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-loss/60 hover:text-loss" aria-label="Dismiss">×</button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading security data…
        </div>
      ) : (
        <>
          {/* Summary stats */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { icon: Users, label: 'Active Members',       value: summary.memberCount,        color: 'text-brand' },
                { icon: Mail,  label: 'Pending Invitations',  value: summary.pendingInviteCount, color: summary.pendingInviteCount > 0 ? 'text-warn' : 'text-gray-400' },
                { icon: Lock,  label: 'Subscription',         value: summary.subscriptionTier.charAt(0).toUpperCase() + summary.subscriptionTier.slice(1), color: 'text-gray-400' },
              ].map(({ icon: Icon, label, value, color }) => (
                <div key={label} className="op-panel p-4 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-3.5 h-3.5 ${color}`} />
                    <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">{label}</p>
                  </div>
                  <p className={`text-2xl font-semibold ${color}`}>{value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Security checks */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2">
              <Shield className="w-4 h-4 text-brand" />
              Security Posture
            </h3>
            <SecurityCheck
              label="Row-Level Security (RLS) enforced"
              description="All organization data is protected by Supabase RLS policies. Org A cannot access Org B data."
              status="ok"
            />
            <SecurityCheck
              label="Role-based access control (RBAC) active"
              description="API endpoints enforce permission checks. Unauthorized actions are rejected server-side."
              status="ok"
            />
            <SecurityCheck
              label="Invitation tokens are time-limited"
              description="Invitations expire after 7 days. Accepted or revoked tokens cannot be reused."
              status="ok"
            />
            <SecurityCheck
              label="Ownership protections enforced"
              description="Only the owner can transfer ownership. Admins cannot self-promote or assign owner role."
              status="ok"
            />
            <SecurityCheck
              label="Audit logging active"
              description="All sensitive admin actions are logged to the organization audit trail."
              status="ok"
            />
            {summary && summary.pendingInviteCount > 5 && (
              <SecurityCheck
                label="High pending invitation count"
                description={`${summary.pendingInviteCount} pending invitations. Review and revoke any that are no longer needed.`}
                status="warn"
              />
            )}
            <SecurityCheck
              label="SSO / SCIM provisioning"
              description="Enterprise SSO and SCIM provisioning are not yet enabled. Planned for a future release."
              status="info"
            />
          </div>

          {/* Organization details */}
          {summary && (
            <div className="op-panel p-4 space-y-3">
              <h3 className="text-sm font-medium text-gray-300">Organization Identity</h3>
              <dl className="space-y-0">
                {[
                  { label: 'Organization', value: summary.name },
                  { label: 'Org ID',       value: summary.id },
                  { label: 'Created',      value: formatDate(summary.createdAt) },
                  { label: 'Owner',        value: summary.ownerDisplayName ?? summary.ownerEmail ?? '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-center justify-between py-2.5 border-b border-surface-4 last:border-0">
                    <dt className="text-sm text-gray-400">{label}</dt>
                    <dd className="text-xs text-gray-200 font-mono">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {!hasRole(['owner']) && (
            <p className="text-xs text-gray-600 text-center">
              Contact the organization owner to modify security settings.
            </p>
          )}
        </>
      )}
    </div>
  );
}
